import type {
  ChargeBreakdown,
  DiscountLine,
  TaxLine,
} from '@components/ProductOffers/modals/ShoppingCartDrawer/PricingSummary/PricingSummary.types';
import type { CartItemDTO, CartPriceDTO, ShoppingCartDTO } from '@t/api.types';
import type { CategoryType, Plan, PlanAddOn } from '@t/productOffering.types';
import { isCouponItem } from '@hooks/useShoppingCart/coupon';
import { findMatchingPlan, sortCartItems } from '@hooks/useShoppingCartDrawer/useShoppingCartDrawer';
import { matchesPriceType } from '@utils/cartPricing';

export { matchesPriceType };

export const fmt = (amount: number | null | undefined): string => {
  if (amount == null) return '$0.00';
  return `$${Math.abs(amount).toFixed(2)}`;
};

export const inferChildType = (child: CartItemDTO): string => {
  if (child.isMandatory || child.type === 'equipment') return 'Equipment';
  return 'Add-On';
};

type ChildCategory = 'ThemePackage' | 'Channel' | 'AddOn' | 'Equipment';

// Display order + visible suffix for each child category. Theme packs first,
// then standalone channels, then generic add-ons, then equipment.
const CHILD_CATEGORY_META: Record<ChildCategory, { suffix: string; order: number }> = {
  ThemePackage: { suffix: 'Theme Pack', order: 0 },
  Channel: { suffix: 'Channel', order: 1 },
  AddOn: { suffix: 'Add-On', order: 2 },
  Equipment: { suffix: 'Equipment', order: 3 },
};

// Classify a priced child for grouping. A theme package carries a channel list;
// a matched Video add-on without one is a standalone channel; anything else is a
// generic add-on. With no resolved plan (offerings not loaded, or caller passed
// none) this degrades to Equipment vs Add-On, matching inferChildType.
const classifyChild = (
  child: CartItemDTO,
  planAddOns: PlanAddOn[],
  planCategory: CategoryType | undefined
): ChildCategory => {
  if (child.type === 'equipment') return 'Equipment';
  const matched = planAddOns.find(
    (a) => a.id === child.productOffering?.id || a.name === child.productOffering?.name
  );
  if (matched && (matched.channels?.length ?? 0) > 0) return 'ThemePackage';
  if (matched && planCategory === 'Video') return 'Channel';
  return 'AddOn';
};

export const extractAlterations = (prices: CartPriceDTO[]): DiscountLine[] => {
  const discounts: DiscountLine[] = [];
  for (const p of prices) {
    for (const alt of p.priceAlteration ?? []) {
      const amt = alt.price?.dutyFreeAmount?.amount ?? alt.price?.taxIncludedAmount?.amount ?? 0;
      if (amt !== 0) {
        discounts.push({ name: alt.name ?? 'Discount', amount: amt });
      }
    }
  }
  return discounts;
};

/** Extract top-level discount entries (priceType === 'discount') from a prices array. */
export const extractDiscountEntries = (prices: CartPriceDTO[], chargeType: 'recurring' | 'oneTime'): DiscountLine[] => {
  const discounts: DiscountLine[] = [];
  for (const p of prices) {
    if (p.priceType !== 'discount') continue;
    const isRecurring = !!p.recurringChargePeriod;
    if (chargeType === 'recurring' && !isRecurring) continue;
    if (chargeType === 'oneTime' && isRecurring) continue;
    const amt = p.price?.dutyFreeAmount?.amount ?? p.price?.taxIncludedAmount?.amount ?? 0;
    if (amt !== 0) {
      discounts.push({ name: p.name ?? 'Discount', amount: amt });
    }
  }
  return discounts;
};

export const getChargesByType = (
  cart: ShoppingCartDTO,
  priceType: 'recurring' | 'oneTime',
  plansByCategory?: Record<CategoryType, Plan[]>
): ChargeBreakdown => {
  const items: ChargeBreakdown['items'] = [];
  const allDiscounts: DiscountLine[] = [];
  const itemLevelDiscounts: DiscountLine[] = [];

  for (const item of sortCartItems(cart.cartItem ?? []).filter((i) => !isCouponItem(i))) {
    const itemPrices = item.itemPrice ?? [];

    const matchingPrices = itemPrices.filter((p) => matchesPriceType(p, priceType));

    // Use the parent's own recurring/oneTime price (discount entries already
    // filtered out by matchesPriceType). Fall back to a mandatory child only
    // when the parent has no matching price entry.
    const parentAmount =
      matchingPrices[0]?.price?.dutyFreeAmount?.amount ?? matchingPrices[0]?.price?.taxIncludedAmount?.amount ?? 0;

    // Resolve the plan behind this cart item so children can be split into theme
    // packages / standalone channels. Absent offerings, planAddOns is empty and
    // classifyChild falls back to Equipment vs Add-On.
    const plan = plansByCategory ? findMatchingPlan(item, plansByCategory) : null;
    const planAddOns = plan?.addOns ?? [];
    const planCategory = plan?.typeName;

    // Collect child items that have a matching price for this type
    // Skip mandatory bundled items — they represent the plan itself (e.g. "Internet 1000")
    const childEntries: Array<{ category: ChildCategory; name: string; amount: number; quantity: number }> = [];
    for (const child of item.childItems ?? []) {
      if (child.isMandatory) continue;
      const childPrices = (child.itemPrice ?? []).filter((p) => matchesPriceType(p, priceType));
      if (childPrices.length === 0) continue;

      const childAmount =
        childPrices[0]?.price?.dutyFreeAmount?.amount ?? childPrices[0]?.price?.taxIncludedAmount?.amount ?? 0;
      childEntries.push({
        category: classifyChild(child, planAddOns, planCategory),
        name: child.productOffering?.name ?? 'Item',
        amount: childAmount,
        quantity: child.quantity ?? 1,
      });
      allDiscounts.push(...extractAlterations(childPrices));
    }

    // Include this plan if the parent or any children matched
    if (matchingPrices.length === 0 && childEntries.length === 0) continue;

    // Group children by category (theme pack → channel → add-on → equipment),
    // alphabetical within each, so the summary reads in a stable order instead of
    // raw backend order.
    const children = childEntries
      .toSorted(
        (a, b) =>
          CHILD_CATEGORY_META[a.category].order - CHILD_CATEGORY_META[b.category].order ||
          a.name.localeCompare(b.name)
      )
      .map((entry) => ({
        name: entry.name,
        amount: entry.amount,
        quantity: entry.quantity,
        typeSuffix: CHILD_CATEGORY_META[entry.category].suffix,
      }));

    items.push({
      name: item.productOffering?.name ?? 'Plan',
      amount: parentAmount,
      children,
    });

    allDiscounts.push(...extractAlterations(matchingPrices));
    itemLevelDiscounts.push(...extractDiscountEntries(itemPrices, priceType));
  }

  // Cart-level totals — read directly from API response
  const cartTotal = (cart.cartTotalPrice ?? []).find((p) => matchesPriceType(p, priceType));

  // Prefer cartTotalPrice discounts (complete GLDS totalDiscounts list, including
  // cart-level promos not attached to any individual item). Fall back to item-level
  // discounts when GLDS does not return totalDiscounts.
  const cartLevelDiscounts = extractDiscountEntries(cart.cartTotalPrice ?? [], priceType);
  allDiscounts.push(...(cartLevelDiscounts.length > 0 ? cartLevelDiscounts : itemLevelDiscounts));

  const dutyFree = cartTotal?.price?.dutyFreeAmount?.amount ?? 0;
  const taxIncluded = cartTotal?.price?.taxIncludedAmount?.amount ?? 0;
  const total = taxIncluded || dutyFree;

  // Tax lines — prefer individual tax entries from the API (e.g. PST, GST),
  // fall back to the computed difference between taxIncluded and dutyFree.
  const taxLines: TaxLine[] = [];
  const taxEntries = (cart.cartTotalPrice ?? []).filter((p) => p.priceType === 'tax' || p.priceType === 'taxCharge');
  if (taxEntries.length > 0) {
    for (const t of taxEntries) {
      const amt = t.price?.taxIncludedAmount?.amount ?? t.price?.dutyFreeAmount?.amount ?? 0;
      if (amt !== 0) {
        taxLines.push({ name: t.name ?? 'Tax', amount: amt });
      }
    }
  } else if (taxIncluded > 0 && dutyFree > 0 && taxIncluded > dutyFree) {
    taxLines.push({ name: 'Estimated Taxes', amount: taxIncluded - dutyFree });
  }

  return { items, discounts: allDiscounts, taxLines, total };
};
